import type { EntityStatus } from '@ms/yammer-data-support';
import { getStatus } from '@ms/yammer-data-support';
import createCachedSelector from 're-reselect';

import type { User, UserFollowedLeadersListFields, YammerState } from '../../state/types';
import { userFollowedLeadersListRequestStatusKey } from '../leadersManagement/actions.types';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';
import { getFulfilledUserFollowedLeaders } from '../userFollowedLeaders/selectors';
import { getFulfilledUsers } from '../users/selectors';

type GetLoadMoreUserFollowedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreUserFollowedLeadersListStatus: GetLoadMoreUserFollowedLeadersListStatus = (state) =>
  getRequestStatus(state, 'UserFollowedLeadersListLoadMore', userFollowedLeadersListRequestStatusKey);

type GetUserFollowedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getUserFollowedLeadersListStatus: GetUserFollowedLeadersListStatus = (state) =>
  getStatus(state.userFollowedLeadersList);

const userFollowedLeaderIds: User['id'][] = [];
type GetUserFollowedLeadersListIdsFromState = (state: YammerState) => User['id'][];
export const getUserFollowedLeadersListIdsFromState: GetUserFollowedLeadersListIdsFromState = (state: YammerState) =>
  state.userFollowedLeadersList.fulfilled?.userFollowedLeaders ?? userFollowedLeaderIds;

type GetUserFollowedLeadersList = (state: YammerState) => UserFollowedLeadersListFields[];
/**
 * @deprecated PERFORMANCE This selects a manually-constructed array of UserFollowedLeadersListFields objects,
 * which will return a new instance and force consumers to re-render whenever ANY user entity in the store gets
 * updated. This should be replaced by a `getUserFollowedLeadersIds` selector, and the top-level consuming component
 * can pass each id to a child component which will use `useUserField` or other "field" selectors for each individual
 * field it requires, while managing its own memoization to avoid unnecessary re-renders.
 */
export const getUserFollowedLeadersList: GetUserFollowedLeadersList = createCachedSelector(
  getUserFollowedLeadersListIdsFromState,
  getFulfilledUserFollowedLeaders,
  getFulfilledUsers,
  (userFollowedLeadersListIds, fulfilledUserFollowedLeaders, fulfilledUsers) =>
    userFollowedLeadersListIds.reduce<UserFollowedLeadersListFields[]>((acc, edgeId) => {
      const userFollowedLeader = fulfilledUserFollowedLeaders[edgeId];

      if (!userFollowedLeader) {
        throw new Error('Missing user followed leader data in state for user followed leaders list edge.');
      }

      const { id, totalFollowedUsers, totalFollowers, directAudienceCount } = userFollowedLeader;
      const { name, jobTitle, avatarUrlTemplate } = fulfilledUsers[id];

      acc.push({
        id,
        avatarUrlTemplate,
        name,
        jobTitle,
        totalFollowedUsers,
        totalFollowers,
        directAudienceCount,
      });

      return acc;
    }, [])
)((_state) => 'cache');

type HasNextPageOfUserFollowedLeadersListAvailable = (state: YammerState) => boolean;
export const hasNextPageOfUserFollowedLeadersListAvailable: HasNextPageOfUserFollowedLeadersListAvailable = (state) =>
  state.userFollowedLeadersList.fulfilled?.hasNextPage ?? false;

type GetUserFollowedLeadersOfficeIdList = (state: YammerState) => string[];
export const getUserFollowedLeadersOfficeIdList: GetUserFollowedLeadersOfficeIdList = createCachedSelector(
  getUserFollowedLeadersListIdsFromState,
  getFulfilledUserFollowedLeaders,
  (userFollowedLeadersListIds, fulfilledUserFollowedLeaders) =>
    userFollowedLeadersListIds.reduce<string[]>((acc, edgeId) => {
      const userFollowedLeader = fulfilledUserFollowedLeaders[edgeId];

      if (!userFollowedLeader) {
        throw new Error('Missing user Fofollowedllowed leader data in state for user followed leaders list edge.');
      }

      const { officeUserId } = userFollowedLeader;
      if (officeUserId) {
        acc.push(officeUserId);
      }

      return acc;
    }, [])
)((_state) => 'cache');
